import { forwardRef } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { Spinner } from "@/components/ui/feedback/Spinner";
import type { ButtonProps, ButtonSize, ButtonVariant, IconButtonProps } from "./Button.types";

interface ButtonOwnerState {
  variant: ButtonVariant;
  size: ButtonSize;
  iconOnly: boolean;
  fullWidth: boolean;
}

/* Styles are the exact compiled output of the pre-migration Tailwind utilities
 * (extracted from the frozen baseline stylesheet), emitted in the same order
 * cva/tailwind-merge produced them: base → variant → size → iconOnly →
 * fullWidth → compound overrides. Do not "clean up" values here — parity with
 * the baseline is verified computed-property-by-property. */

const buttonBase: CSSObject = {
  display: "inline-flex",
  minWidth: "0px",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-button-radius)",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(0,0,.2,1)",
  transitionDuration: ".15s",
  "&:focus-visible": {
    "--tw-ring-offset-width": "0px",
    "--tw-ring-offset-color": "#fff",
    "--tw-ring-color": "hsl(var(--bfrs-color-border-focus) / .25)",
    "--tw-ring-offset-shadow": "0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color)",
    "--tw-ring-shadow": "0 0 0 calc(2px + var(--tw-ring-offset-width)) var(--tw-ring-color)",
    boxShadow: "var(--tw-ring-offset-shadow),var(--tw-ring-shadow),var(--tw-shadow, 0 0 #0000)"
  },
  "&:disabled": {
    pointerEvents: "none",
    opacity: ".55"
  }
};

const buttonVariantStyles: Record<Exclude<ButtonVariant, "unstyled">, CSSObject> = {
  primary: {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
    fontWeight: 700,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-hover) / var(--tw-bg-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-active) / var(--tw-bg-opacity, 1))"
    }
  },
  secondary: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    fontWeight: 400,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
    }
  },
  tertiary: {
    height: "auto",
    borderRadius: "var(--bfrs-radius-sm)",
    borderWidth: "1px",
    borderColor: "transparent",
    backgroundColor: "transparent",
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))",
    textDecorationLine: "underline",
    textUnderlineOffset: "4px",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-primary-hover) / var(--tw-text-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-accent-soft) / var(--tw-bg-opacity, 1))"
    }
  },
  outline: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-accent-soft) / var(--tw-bg-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-raised) / var(--tw-bg-opacity, 1))"
    }
  },
  ghost: {
    backgroundColor: "transparent",
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
      "--tw-text-opacity": "1",
      color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
    }
  },
  destructive: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))"
    }
  },
  danger: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-danger) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-danger-soft) / var(--tw-bg-opacity, 1))"
    }
  },
  link: {
    height: "auto",
    borderRadius: "var(--bfrs-radius-sm)",
    borderWidth: "1px",
    borderColor: "transparent",
    backgroundColor: "transparent",
    padding: 0,
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))",
    textUnderlineOffset: "4px",
    "&:hover": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
      textDecorationLine: "underline"
    },
    "&:active": {
      "--tw-bg-opacity": "1",
      backgroundColor: "hsl(var(--bfrs-color-accent-soft) / var(--tw-bg-opacity, 1))"
    }
  }
};

const buttonSizeStyles: Record<ButtonSize, CSSObject> = {
  xs: {
    height: "26px",
    gap: ".375rem",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    fontSize: "12px",
    lineHeight: "normal"
  },
  sm: {
    height: "1.75rem",
    gap: ".375rem",
    paddingLeft: ".75rem",
    paddingRight: ".75rem",
    fontSize: "12px"
  },
  md: {
    height: "30px",
    gap: ".5rem",
    paddingLeft: "18px",
    paddingRight: "18px",
    fontSize: "12px"
  },
  lg: {
    height: "2.5rem",
    gap: ".625rem",
    paddingLeft: "1.25rem",
    paddingRight: "1.25rem",
    fontSize: ".875rem",
    lineHeight: "1.25rem"
  }
};

const buttonIconOnlyStyle: CSSObject = {
  aspectRatio: "1 / 1",
  paddingLeft: 0,
  paddingRight: 0
};

const buttonFullWidthStyle: CSSObject = {
  width: "100%"
};

const buttonUnstyledBase: CSSObject = {
  appearance: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
  border: 0,
  background: "transparent",
  color: "inherit",
  font: "inherit",
  letterSpacing: "inherit",
  padding: 0,
  margin: 0,
  textAlign: "inherit",
  textDecoration: "none",
  outline: "none",
  cursor: "pointer",
  "&:disabled": {
    cursor: "default",
    pointerEvents: "none"
  }
};

const buttonCompoundNoBox: CSSObject = {
  height: "auto",
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0
};

const buttonIconOnlyCompound: Record<ButtonSize, CSSObject> = {
  xs: { height: "26px", width: "26px", paddingLeft: 0, paddingRight: 0 },
  sm: { height: "1.75rem", width: "1.75rem", paddingLeft: 0, paddingRight: 0 },
  md: { height: "2rem", width: "2rem", paddingLeft: 0, paddingRight: 0 },
  lg: { width: "2.5rem" }
};

const ButtonRoot = styled("button", {
  label: "AgenticButton-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: ButtonOwnerState }>(({ ownerState }) =>
  ownerState.variant === "unstyled"
    ? [buttonUnstyledBase, ownerState.fullWidth && buttonFullWidthStyle]
    : [
        buttonBase,
        buttonVariantStyles[ownerState.variant],
        buttonSizeStyles[ownerState.size],
        ownerState.iconOnly && buttonIconOnlyStyle,
        ownerState.fullWidth && buttonFullWidthStyle,
        (ownerState.variant === "tertiary" || ownerState.variant === "link") && buttonCompoundNoBox,
        ownerState.iconOnly && buttonIconOnlyCompound[ownerState.size]
      ]
);

const ButtonLabel = styled("span", {
  label: "AgenticButton-label"
})({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const spinnerSize = {
  xs: "sm",
  sm: "sm",
  md: "sm",
  lg: "md"
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      fullWidth,
      iconOnly,
      leftIcon,
      rightIcon,
      loading,
      loadingLabel = "Loading",
      disabled,
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    const ownerState: ButtonOwnerState = {
      variant,
      size,
      iconOnly: Boolean(iconOnly),
      fullWidth: Boolean(fullWidth)
    };

    return (
      <ButtonRoot
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        aria-label={iconOnly && typeof children === "string" ? children : props["aria-label"]}
        ownerState={ownerState}
        className={className}
        {...props}
      >
        {loading ? <Spinner size={spinnerSize[size]} label={loadingLabel} /> : leftIcon}
        {variant !== "unstyled" && !iconOnly && children ? <ButtonLabel>{children}</ButtonLabel> : children}
        {!loading && rightIcon}
      </ButtonRoot>
    );
  }
);

Button.displayName = "Button";

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({ icon, size = "md", variant = "ghost", ...props }, ref) => (
  <Button ref={ref} size={size} variant={variant} iconOnly {...props}>
    {props.loading ? null : icon}
  </Button>
));

IconButton.displayName = "IconButton";
